import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Card,
    Descriptions,
    Table,
    Button,
    Space,
    Divider,
    Typography,
    notification,
    Row,
    Col,
    Statistic,
    Alert,
    Tag,
    Grid,
    Spin
} from "antd";
import {
    ArrowLeftOutlined,
    PrinterOutlined
} from "@ant-design/icons";
import { proformaService } from "../../services/proformaService";

const { Title, Text } = Typography;
const { Column } = Table;
const { useBreakpoint } = Grid;

const ProformaDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchInvoiceDetails();
    }, [id]);

    const fetchInvoiceDetails = async () => {
        try {
            setLoading(true);
            const response = await proformaService.getProformaInvoice(id);
            if (response.success) {
                setInvoice(response.data);
            }
        } catch (error) {
            notification.error({
                message: "Error",
                description:
                    error.response?.data?.error ||
                    "Failed to fetch proforma details",
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = async () => {
        try {
            const response = await proformaService.exportProformaInvoice(id);
            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Proforma_${invoice?.invoiceNumber || id}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Download failed", error);
            notification.error({ message: "Failed to download proforma" });
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" tip="Loading Proforma Details..." />
            </div>
        );
    }

    if (!invoice) {
        return (
            <Card style={{ margin: isMobile ? '12px' : '24px' }}>
                <Alert
                    message="Proforma Not Found"
                    type="error"
                    showIcon
                    action={
                        <Button onClick={() => navigate("/proforma")}>
                            Back to Proforma Invoices
                        </Button>
                    }
                />
            </Card>
        );
    }

    return (
        <div style={{ padding: isMobile ? '12px' : '24px' }}>
            <Space direction="vertical" size={isMobile ? "middle" : "large"} style={{ width: "100%" }}>
                <Card bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
                    <Row justify="space-between" align="middle" gutter={[16, 16]}>
                        <Col xs={24} sm={16}>
                            <Space align="start" size="middle">
                                <Button
                                    icon={<ArrowLeftOutlined />}
                                    onClick={() => navigate("/proforma")}
                                    type="text"
                                    style={{ marginTop: 4 }}
                                />
                                <div>
                                    <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
                                        Proforma Invoice #{invoice.invoiceNumber}
                                    </Title>
                                    <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                                        Issued: {new Date(invoice.invoiceDate).toLocaleDateString()}
                                    </Text>
                                </div>
                            </Space>
                        </Col>
                        <Col xs={24} sm={8} style={{ textAlign: isMobile ? 'left' : 'right' }}>
                            <Button 
                                icon={<PrinterOutlined />} 
                                type="primary" 
                                onClick={handlePrint}
                                block={isMobile}
                                size={isMobile ? "middle" : "large"}
                            >
                                Print Proforma
                            </Button>
                        </Col>
                    </Row>
                </Card>

                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={16}>
                        <Card title="Proforma Details" headStyle={{ fontWeight: 600 }}>
                            <Descriptions 
                                column={isMobile ? 1 : 2} 
                                bordered 
                                size={isMobile ? "small" : "middle"}
                            >
                                <Descriptions.Item label="Proforma Number">
                                    <strong>{invoice.invoiceNumber}</strong>
                                </Descriptions.Item>
                                <Descriptions.Item label="Date">
                                    {new Date(invoice.invoiceDate).toLocaleDateString()}
                                </Descriptions.Item>

                                <Descriptions.Item label="Issued By" span={isMobile ? 1 : 2}>
                                    <Space direction="vertical" size={0}>
                                        <Text strong>{invoice.company?.companyName}</Text>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>GST: {invoice.company?.gstn}</Text>
                                    </Space>
                                </Descriptions.Item>

                                <Descriptions.Item label="Customer" span={isMobile ? 1 : 2}>
                                    <Space direction="vertical" size={0}>
                                        <Text strong>{invoice.receiver?.customerName}</Text>
                                        <Text style={{ fontSize: '13px' }}>
                                            {invoice.receiver?.customerAddress}
                                            {invoice.receiver?.district ? `, ${invoice.receiver.district}` : ""}, 
                                            Maharashtra {invoice.receiver?.pincode || ""}
                                        </Text>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            GSTN: {invoice.receiver?.gstn || 'N/A'}
                                        </Text>
                                    </Space>
                                </Descriptions.Item>

                                <Descriptions.Item label="Contract Amount">
                                    <strong>₹{Math.round(invoice.contractAmount || 0).toLocaleString()}</strong>
                                </Descriptions.Item>
                                <Descriptions.Item label="PO / Contract Ref">
                                    {invoice.poNumber || 'N/A'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Consumer Number" span={isMobile ? 1 : 2}>
                                    <Tag color="orange" style={{ fontWeight: 600 }}>{invoice.consumerNo || 'N/A'}</Tag>
                                </Descriptions.Item>
                            </Descriptions>

                            <Divider orientation="left" style={{ marginTop: 32 }}>Items</Divider>
                            <Table
                                dataSource={invoice.items || []}
                                rowKey="id"
                                pagination={false}
                                size="small"
                                scroll={{ x: 500 }}
                            >
                                <Column
                                    title="Description"
                                    dataIndex="description"
                                    key="description"
                                    render={(text, record) => (
                                        <Space direction="vertical" size={0}>
                                            <Text strong>{text || record.item?.itemName || 'N/A'}</Text>
                                            {record.natureKey && (
                                                <Tag color="blue" style={{ fontSize: '10px', marginTop: '4px' }}>
                                                    {record.natureKey.toUpperCase()}
                                                </Tag>
                                            )}
                                        </Space>
                                    )}
                                />
                                <Column
                                    title="Qty"
                                    dataIndex="quantity"
                                    key="quantity"
                                    align="right"
                                    width={70}
                                />
                                <Column
                                    title="Rate"
                                    dataIndex="rate"
                                    key="rate"
                                    align="right"
                                    render={(rate) => `₹${Math.round(rate || 0).toLocaleString()}`}
                                />
                                <Column
                                    title="Total"
                                    dataIndex="totalAmount"
                                    key="totalAmount"
                                    align="right"
                                    render={(amount, record) => (
                                        <Space direction="vertical" align="end" size={0}>
                                            <strong>₹{Math.round(amount || 0).toLocaleString()}</strong>
                                            <Text type="secondary" style={{ fontSize: '10px' }}>
                                                Inc. GST ({record.gstRate}%)
                                            </Text>
                                        </Space>
                                    )}
                                />
                            </Table>

                            {invoice.amountInWords && (
                                <div style={{ marginTop: 24, padding: 12, backgroundColor: '#fafafa', borderRadius: 8 }}>
                                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>Amount in Words:</Text>
                                    <Text italic strong style={{ color: '#595959' }}>{invoice.amountInWords}</Text>
                                </div>
                            )}
                        </Card>
                    </Col>

                    <Col xs={24} lg={8}>
                        <Card title="Payment Summary" headStyle={{ fontWeight: 600 }}>
                            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text type="secondary">Total Taxable</Text>
                                    <Text strong>₹{Math.round(invoice.totalBeforeTax || 0).toLocaleString()}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text type="secondary">Total Tax</Text>
                                    <Text strong>₹{Math.round(invoice.totalTax || (invoice.items || []).reduce((acc, item) => acc + (parseFloat(item.taxAmount || 0)), 0)).toLocaleString()}</Text>
                                </div>
                                <Divider style={{ margin: '12px 0' }} />
                                <Statistic
                                    title={<Text strong style={{ fontSize: '16px' }}>Grand Total</Text>}
                                    value={Math.round(invoice.totalAfterTax || 0)}
                                    prefix="₹"
                                    valueStyle={{ color: "#1890ff", fontSize: isMobile ? 24 : 28, fontWeight: 700 }}
                                />
                            </Space>
                        </Card>

                        {invoice.taxDetails && invoice.taxDetails.length > 0 && (
                            <Card title="Tax Breakdown" style={{ marginTop: 16 }} size="small">
                                <Descriptions column={1} size="small" colon={false}>
                                    <Descriptions.Item label={<Text type="secondary">CGST</Text>}>
                                        <Text strong>₹{Math.round(invoice.taxDetails[0].cgstAmount || 0).toLocaleString()}</Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label={<Text type="secondary">SGST</Text>}>
                                        <Text strong>₹{Math.round(invoice.taxDetails[0].sgstAmount || 0).toLocaleString()}</Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label={<Text type="secondary">IGST</Text>}>
                                        <Text strong>₹{Math.round(invoice.taxDetails[0].igstAmount || 0).toLocaleString()}</Text>
                                    </Descriptions.Item>
                                </Descriptions>
                            </Card>
                        )}
                    </Col>
                </Row>
            </Space>
        </div>
    );
};

export default ProformaDetail;
